// Los formularios de texto del documento al cliente (F4b · D2): datos del cliente, información comercial,
// condiciones y firma. Piezas SIN estado de página: reciben el valor y avisan cada cambio (F5 las monta tal cual).
// Los rótulos son los que lee una persona; ningún nombre interno aparece en pantalla.
import { useId } from 'react';
import type {
  CondicionesExtra,
  DatosDelCliente,
  FirmaDelDocumento,
  InformacionComercial,
} from '../../types/pricer-datec.types';
import { FORMAS_DE_PAGO, PLAZOS_DE_ENTREGA } from '../../lib/opciones-condiciones';
import { SelectorConOtro } from '../SelectorConOtro';
import { ayuda, enlace, error } from './estilos';
import { ContadorDeLimite } from './ContadorDeLimite';

export const MENSAJE_TELEFONO = 'El teléfono sólo admite números y + ( ) - . /';
export const MENSAJE_CORREO =
  'El correo no tiene forma de correo (por ejemplo, nombre@empresa.com)';
const ES_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ES_TELEFONO = /^[0-9+()\-./ ]*$/;

export const correoMalo = (v: string) => v.trim() !== '' && !ES_CORREO.test(v.trim());
export const telefonoMalo = (v: string) => !ES_TELEFONO.test(v);

interface CampoProps {
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  max: number;
  disabled?: boolean;
  problema?: string | null;
  placeholder?: string;
  tipo?: 'text' | 'email' | 'tel';
  /** Sugerencias de texto libre (la persona puede escribir otra cosa). */
  sugerencias?: string[];
  ayudaCorta?: string;
}

export function Campo({
  etiqueta,
  valor,
  onChange,
  max,
  disabled,
  problema,
  placeholder,
  tipo = 'text',
  sugerencias,
  ayudaCorta,
}: CampoProps) {
  const id = useId();
  const lista = `${id}-lista`;
  return (
    <div>
      <label className="pd-field-label" htmlFor={id}>
        {etiqueta}
      </label>
      <input
        id={id}
        className="pd-field-input"
        type={tipo}
        value={valor}
        maxLength={max}
        // El texto completo al pasar el mouse: «Lema» o «Condición comercial» se cortan en el ancho de la columna.
        title={valor.length > 28 ? valor : undefined}
        disabled={disabled}
        placeholder={placeholder}
        list={sugerencias && sugerencias.length > 0 ? lista : undefined}
        aria-invalid={problema ? true : undefined}
        aria-describedby={problema ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {sugerencias && sugerencias.length > 0 && (
        <datalist id={lista}>
          {sugerencias.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
      <ContadorDeLimite largo={valor.length} max={max} />
      {ayudaCorta && !problema && <div style={ayuda}>{ayudaCorta}</div>}
      {problema && (
        <div id={`${id}-error`} role="alert" style={error}>
          {problema}
        </div>
      )}
    </div>
  );
}

const rejilla: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };

export function DatosDelClienteForm({
  valor,
  onChange,
  soloLectura,
}: {
  valor: DatosDelCliente;
  onChange: (v: DatosDelCliente) => void;
  soloLectura?: boolean;
}) {
  const cambiar = (p: Partial<DatosDelCliente>) => !soloLectura && onChange({ ...valor, ...p });
  return (
    <div style={rejilla}>
      <div style={{ gridColumn: '1 / -1' }}>
        <Campo
          etiqueta="Dirección del cliente"
          valor={valor.direccion}
          max={200}
          disabled={soloLectura}
          onChange={(v) => cambiar({ direccion: v })}
        />
      </div>
      <Campo
        etiqueta="Contacto"
        valor={valor.contacto}
        max={120}
        disabled={soloLectura}
        ayudaCorta="Se escribe a mano: nombre y apellido de quien recibe la cotización."
        onChange={(v) => cambiar({ contacto: v })}
      />
      <Campo
        etiqueta="Cargo del contacto"
        valor={valor.cargo}
        max={120}
        disabled={soloLectura}
        onChange={(v) => cambiar({ cargo: v })}
      />
      <Campo
        etiqueta="Correo electrónico del contacto"
        tipo="email"
        valor={valor.correo}
        max={254}
        disabled={soloLectura}
        problema={correoMalo(valor.correo) ? MENSAJE_CORREO : null}
        onChange={(v) => cambiar({ correo: v })}
      />
      <Campo
        etiqueta="Teléfono del contacto"
        tipo="tel"
        valor={valor.telefono}
        max={40}
        disabled={soloLectura}
        problema={telefonoMalo(valor.telefono) ? MENSAJE_TELEFONO : null}
        onChange={(v) => cambiar({ telefono: v })}
      />
    </div>
  );
}

export function InformacionComercialForm({
  valor,
  onChange,
  soloLectura,
  sugerenciasDePreventa = [],
  sectorSugerido,
}: {
  valor: InformacionComercial;
  onChange: (v: InformacionComercial) => void;
  soloLectura?: boolean;
  /** Nombres de usuarios activos de la empresa; el campo sigue siendo texto libre. */
  sugerenciasDePreventa?: string[];
  /** El sector que dice la ficha del cliente; se ofrece, no se impone. */
  sectorSugerido?: string;
}) {
  const cambiar = (p: Partial<InformacionComercial>) =>
    !soloLectura && onChange({ ...valor, ...p });
  const ofrecerSector = !!sectorSugerido && valor.sector.trim() !== sectorSugerido.trim();
  return (
    <div style={rejilla}>
      <div style={{ gridColumn: '1 / -1' }}>
        <Campo
          etiqueta="Proyecto"
          valor={valor.proyecto}
          max={200}
          disabled={soloLectura}
          onChange={(v) => cambiar({ proyecto: v })}
        />
      </div>
      <div>
        <Campo
          etiqueta="Sector"
          valor={valor.sector}
          max={80}
          disabled={soloLectura}
          onChange={(v) => cambiar({ sector: v })}
        />
        {ofrecerSector && !soloLectura && (
          <div style={ayuda}>
            La ficha del cliente dice «{sectorSugerido}».{' '}
            <button
              type="button"
              style={enlace}
              onClick={() => cambiar({ sector: sectorSugerido! })}
            >
              Usar ese sector
            </button>
          </div>
        )}
      </div>
      <Campo
        etiqueta="Preventa"
        valor={valor.preventa}
        max={120}
        disabled={soloLectura}
        sugerencias={sugerenciasDePreventa}
        ayudaCorta="Escribí el nombre o elegí uno de la lista."
        onChange={(v) => cambiar({ preventa: v })}
      />
      <div style={{ gridColumn: '1 / -1' }}>
        <Campo
          etiqueta="Condición comercial"
          valor={valor.condicion}
          max={200}
          disabled={soloLectura}
          onChange={(v) => cambiar({ condicion: v })}
        />
      </div>
    </div>
  );
}

export function CondicionesComercialesForm({
  formaPago,
  plazoEntrega,
  extra,
  validezDias,
  onChange,
  soloLectura,
}: {
  formaPago: string;
  plazoEntrega: string;
  extra: CondicionesExtra;
  validezDias: number;
  onChange: (p: {
    formaPago?: string;
    plazoEntrega?: string;
    condicionesExtra?: CondicionesExtra;
  }) => void;
  soloLectura?: boolean;
}) {
  const cambiarExtra = (p: Partial<CondicionesExtra>) =>
    !soloLectura && onChange({ condicionesExtra: { ...extra, ...p } });
  return (
    <div style={rejilla}>
      <div>
        <label className="pd-field-label" htmlFor="pd-doc-moneda">
          Moneda
        </label>
        <input id="pd-doc-moneda" className="pd-field-input" value="USD" disabled readOnly />
        <div style={ayuda}>Fija: el documento sale siempre en dólares.</div>
      </div>
      <div>
        <label className="pd-field-label" htmlFor="pd-doc-vigencia">
          Vigencia
        </label>
        <input
          id="pd-doc-vigencia"
          className="pd-field-input"
          value={`${validezDias} días desde la fecha de emisión`}
          disabled
          readOnly
        />
        <div style={ayuda}>Sale de la validez de la cotización.</div>
      </div>
      <SelectorConOtro
        etiqueta="Forma de pago"
        opciones={FORMAS_DE_PAGO}
        valor={formaPago}
        disabled={soloLectura}
        onChange={(v) => !soloLectura && onChange({ formaPago: v })}
      />
      <SelectorConOtro
        etiqueta="Plazo de entrega"
        opciones={PLAZOS_DE_ENTREGA}
        valor={plazoEntrega}
        disabled={soloLectura}
        onChange={(v) => !soloLectura && onChange({ plazoEntrega: v })}
      />
      <Campo
        etiqueta="Cobertura y garantía"
        valor={extra.cobertura}
        max={200}
        disabled={soloLectura}
        onChange={(v) => cambiarExtra({ cobertura: v })}
      />
      <Campo
        etiqueta="Impuestos"
        valor={extra.impuestos}
        max={200}
        disabled={soloLectura}
        onChange={(v) => cambiarExtra({ impuestos: v })}
      />
      <div style={{ gridColumn: '1 / -1' }}>
        <label className="pd-field-label" htmlFor="pd-doc-otros">
          Otras condiciones
        </label>
        <textarea
          id="pd-doc-otros"
          className="pd-field-input"
          rows={2}
          maxLength={500}
          value={extra.otros}
          disabled={soloLectura}
          onChange={(e) => cambiarExtra({ otros: e.target.value })}
        />
        <ContadorDeLimite largo={extra.otros.length} max={500} />
      </div>
    </div>
  );
}

export function FirmaForm({
  valor,
  ejemplo,
  onChange,
  soloLectura,
}: {
  valor: FirmaDelDocumento;
  /** El ejemplo de la Configuración: se copia al armar la cotización y desde ahí se edita. */
  ejemplo?: FirmaDelDocumento;
  onChange: (v: FirmaDelDocumento) => void;
  soloLectura?: boolean;
}) {
  const cambiar = (p: Partial<FirmaDelDocumento>) => !soloLectura && onChange({ ...valor, ...p });
  const distinto = !!ejemplo && JSON.stringify(ejemplo) !== JSON.stringify(valor);
  return (
    <div>
      <div style={rejilla}>
        <Campo
          etiqueta="Saludo"
          valor={valor.saludo}
          max={80}
          disabled={soloLectura}
          onChange={(v) => cambiar({ saludo: v })}
        />
        <Campo
          etiqueta="Equipo preventa"
          valor={valor.equipo}
          max={80}
          disabled={soloLectura}
          ayudaCorta="Sale bajo el saludo, por ejemplo «Equipo Preventa»."
          onChange={(v) => cambiar({ equipo: v })}
        />
        <Campo
          etiqueta="Razón social"
          valor={valor.razonSocial}
          max={80}
          disabled={soloLectura}
          onChange={(v) => cambiar({ razonSocial: v })}
        />
        <Campo
          etiqueta="Lema"
          valor={valor.lema}
          max={80}
          disabled={soloLectura}
          onChange={(v) => cambiar({ lema: v })}
        />
        <Campo
          etiqueta="Correo del comercial"
          tipo="email"
          valor={valor.correoComercial}
          max={254}
          disabled={soloLectura}
          problema={correoMalo(valor.correoComercial) ? MENSAJE_CORREO : null}
          onChange={(v) => cambiar({ correoComercial: v })}
        />
      </div>
      {!soloLectura && distinto && (
        <div style={{ marginTop: 6 }}>
          <button type="button" style={enlace} onClick={() => onChange({ ...ejemplo! })}>
            Volver al ejemplo de la Configuración
          </button>
        </div>
      )}
    </div>
  );
}
